import React, { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "react-toastify";

import { pmndpAPI } from "../../../services/pmndp.api";
import Loader from "../../../components/Loader";

import PMNDPModal from './PMNDPModal';
import {
    PMNDPFormField,
    PMNDPFormSection,
    PMNDPFormButtons
} from './PMNDPForm';
import styles from './pmndp-enhanced.module.css';

const BookAnAppointment = ({ onClose, patient_name, patient_id, abha_number, getDiaList }) => {
    const { defaultDate, maxDate } = useMemo(() => {
        try {
            const today = new Date();
            if (isNaN(today.getTime())) throw new Error('Invalid date');
            
            const yyyy = today.getFullYear();
            const mm = String(today.getMonth() + 1).padStart(2, '0');
            const dd = String(today.getDate()).padStart(2, '0');
            const defaultDate = `${yyyy}-${mm}-${dd}`;
            
            const maxDateObj = new Date(today);
            maxDateObj.setDate(today.getDate() + 7);
            if (isNaN(maxDateObj.getTime())) throw new Error('Invalid max date');
            
            const maxyyyy = maxDateObj.getFullYear();
            const maxmm = String(maxDateObj.getMonth() + 1).padStart(2, '0');
            const maxdd = String(maxDateObj.getDate()).padStart(2, '0');
            const maxDate = `${maxyyyy}-${maxmm}-${maxdd}`;
            
            return { defaultDate, maxDate };
        } catch (error) {
            console.error('Error calculating dates:', error);
            const fallbackDate = '2024-01-01';
            return { defaultDate: fallbackDate, maxDate: fallbackDate };
        }
    }, []);
    
    const [isPageLoading, setIsPageLoading] = useState(false);

    const [appointmentDate, setAppointmentDate] = useState(defaultDate);
    const [tableData, setTableData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedSlot, setSelectedSlot] = useState(null);

    const getSlotsByDate = useCallback(async () => {
        try {
            setIsLoading(true);
            const resp = await pmndpAPI.getSlotsByDate({ slot_date: appointmentDate, patient_view: '' });
            const slotlist = resp?.data?.data?.dataGet;
            setTableData(Array.isArray(slotlist) ? slotlist : []);
        } catch (error) {
            console.error("Error fetching slots:", error);
            toast.error("Failed to load available slots");
            setTableData([]);
        } finally {
            setIsLoading(false);
        }
    }, [appointmentDate]);

    useEffect(() => {
        setSelectedSlot(null);
        getSlotsByDate();
    }, [appointmentDate, getSlotsByDate]);

    const patientIdValue = useMemo(() => patient_id || "", [patient_id]);

    const handleSlotChange = useCallback((machine, slot) => {
        try {
            if (!slot) {
                toast.error('Invalid slot selected');
                return;
            }
            setSelectedSlot({
                slot_id: slot?.slot_id || slot?.id || "",
                ...slot
            });
        } catch (error) {
            console.error('Error selecting slot:', error);
            toast.error('Failed to select slot');
        }
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedSlot || !selectedSlot.slot_id) {
            toast.error("Please select a slot.");
            return;
        }

        const payload = {
            id: "",
            appointment_date: appointmentDate,
            patient_id: patientIdValue,
            slot_id: selectedSlot.slot_id,
            options: selectedSlot.slot_id
        };

        setIsPageLoading(true);
        try {
            const resp = await pmndpAPI.scheduleDialysis(payload);
            
            if (resp && resp.success) {
                toast.success(`Your next session scheduled successfully.`);
                setTimeout(() => {
                    try {
                        if (typeof onClose === 'function') onClose();
                        if (typeof getDiaList === 'function') getDiaList();
                    } catch (callbackError) {
                        console.error('Error in callback functions:', callbackError);
                        toast.error("Error occurred after scheduling appointment");
                    }
                }, 1200);
            } else {
                const errorMsg = resp?.msg || resp?.message || "Failed to schedule appointment";
                toast.error(errorMsg, { className: "custom-toast-error" });
            }
        } catch (error) {
            console.error('Error scheduling appointment:', error);
            const errorMsg = error?.response?.data?.message || error?.message || "Error occurred while scheduling appointment";
            toast.error(errorMsg);
        } finally {
            setIsPageLoading(false);
        }

    };

    return (
        <>
            
            {isPageLoading && <Loader />}

            <PMNDPModal
                isOpen={true}
                onClose={onClose}
                title="Book Appointment"
                icon="fa-calendar-check"
                size="xlarge"
                footer={
                    <PMNDPFormButtons
                        onSave={handleSubmit}
                        onCancel={onClose}
                        saveText="Book Appointment"
                        cancelText="Cancel"
                        isLoading={isPageLoading}
                        disabled={!selectedSlot}
                    />
                }
            >
                <form onSubmit={handleSubmit}>
                    <PMNDPFormSection title="Patient Information" icon="fa-user">
                        <div style={{ 
                            display: 'grid', 
                            gridTemplateColumns: '1fr 1fr', 
                            gap: '20px',
                            background: '#f8fafc',
                            padding: '20px',
                            borderRadius: '12px',
                            border: '1px solid #e2e8f0'
                        }}>
                            <div>
                                <strong style={{ color: '#374151', fontSize: '14px' }}>ABHA ID / Patient ID: </strong>
                                <span style={{ color: '#6b7280', marginTop: '4px', fontSize: '16px' }}>{abha_number}</span>
                            </div>
                            <div>
                                <strong style={{ color: '#374151', fontSize: '14px' }}>Patient Name: </strong>
                                <span style={{ color: '#6b7280', marginTop: '4px', fontSize: '16px' }}>{patient_name}</span>
                            </div>
                        </div>
                    </PMNDPFormSection>

                    <PMNDPFormSection title="Appointment Details" icon="fa-calendar">
                        <PMNDPFormField
                            label="Appointment Date"
                            type="date"
                            value={appointmentDate}
                            onChange={(e) => {
                                try {
                                    const newDate = e.target.value;
                                    if (!newDate) {
                                        toast.error('Please select a valid date');
                                        return;
                                    }
                                    setAppointmentDate(newDate);
                                } catch (error) {
                                    console.error('Error setting date:', error);
                                    toast.error('Invalid date selected');
                                }
                            }}
                            min={defaultDate}
                            max={maxDate}
                            required
                        />
                    </PMNDPFormSection>

                    <PMNDPFormSection title="Available Slots" icon="fa-clock-o">
                        {isLoading ? (
                            <div className={styles['pmndp-loading']}>
                                <div className={styles['pmndp-spinner']}></div>
                            </div>
                        ) : Array.isArray(tableData) && tableData.length > 0 ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                {tableData.map((machine, idx) => (
                                    <div key={machine.id || idx} style={{
                                        background: '#f8fafc',
                                        border: '2px solid #e2e8f0',
                                        borderRadius: '12px',
                                        padding: '20px',
                                        transition: 'all 0.3s ease'
                                    }}>
                                        <div style={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            gap: '15px',
                                            marginBottom: '15px',
                                            paddingBottom: '10px',
                                            borderBottom: '1px solid #e2e8f0'
                                        }}>
                                            <div style={{ 
                                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                color: 'white',
                                                padding: '8px 12px',
                                                borderRadius: '8px',
                                                fontSize: '14px',
                                                fontWeight: '600'
                                            }}>
                                                {machine.sr_no}
                                            </div>
                                            <div>
                                                <div style={{ fontWeight: '600', color: '#374151' }}>Machine Type</div>
                                                <div style={{ color: '#6b7280', fontSize: '14px' }}>{machine.machine_type}</div>
                                            </div>
                                        </div>
                                        
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                            {Array.isArray(machine.slots) && machine.slots.length > 0 ? (
                                                machine.slots.map((slot, sidx) => (
                                                    <label key={slot.slot_id || sidx} style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '8px',
                                                        padding: '10px 15px',
                                                        background: selectedSlot?.slot_id === slot.slot_id ? 
                                                            'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'white',
                                                        color: selectedSlot?.slot_id === slot.slot_id ? 'white' : '#374151',
                                                        border: '2px solid',
                                                        borderColor: selectedSlot?.slot_id === slot.slot_id ? '#667eea' : '#e2e8f0',
                                                        borderRadius: '8px',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.3s ease',
                                                        fontSize: '14px',
                                                        fontWeight: '600'
                                                    }}>
                                                        <input
                                                            type="radio"
                                                            name="slot_selection"
                                                            value={slot.slot_id}
                                                            onChange={() => handleSlotChange(machine, slot)}
                                                            style={{ margin: 0 }}
                                                        />
                                                        <span>{slot.start_time} - {slot.end_time}</span>
                                                    </label>
                                                ))
                                            ) : (
                                                <div style={{
                                                    padding: '20px',
                                                    textAlign: 'center',
                                                    color: '#6b7280',
                                                    fontStyle: 'italic'
                                                }}>
                                                    No slots available
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className={styles['pmndp-empty-state']}>
                                <div className={styles['pmndp-empty-icon']}>
                                    <i className="fa fa-calendar-times"></i>
                                </div>
                                <div className={styles['pmndp-empty-title']}>No slots available</div>
                                <div className={styles['pmndp-empty-description']}>No appointment slots are available for the selected date. Please try a different date.</div>
                            </div>
                        )}
                    </PMNDPFormSection>
                </form>
            </PMNDPModal>
        </>
    );
};

export default BookAnAppointment;
